import { z } from 'zod';

/**
 * Live Google reviews snapshot served by `GET /website/google-reviews`.
 *
 * Source: Google Places API (New) "Place Details" for the business's place id
 * - it returns the overall rating, the total review count and at most FIVE
 * reviews (Google's cap; the review text, star rating, author name, author
 * photo and relative date - never the reviewer's trip photos). The API caches
 * the snapshot in Redis so Google is hit a few times a day at most.
 *
 * `configured: false` means no API key / place id is set in Secrets Manager
 * (or the last fetch failed) - sections fall back to their typed content.
 */
export const GoogleReviewSchema = z.object({
  /** Stable id (Google's review resource name) for React keys / dedupe. */
  id: z.string(),
  authorName: z.string(),
  authorPhotoUrl: z.string().nullable(),
  /** 1-5 stars. */
  rating: z.number().min(0).max(5),
  text: z.string(),
  /** Google's own phrasing, e.g. "2 months ago". */
  relativeTime: z.string().nullable(),
  /** ISO timestamp of the review, when Google provides one. */
  publishedAt: z.string().nullable(),
  /** Link to the review / the business on Google Maps. */
  url: z.string().nullable(),
});
export type GoogleReview = z.infer<typeof GoogleReviewSchema>;

export const GoogleReviewsSnapshotSchema = z.object({
  configured: z.boolean(),
  /** Overall rating, e.g. 4.4. Null when unconfigured. */
  rating: z.number().nullable(),
  /** Total number of Google reviews, e.g. 512. Null when unconfigured. */
  ratingCount: z.number().int().nullable(),
  reviews: z.array(GoogleReviewSchema),
  /** "Write a review" / business page link on Google Maps. */
  mapsUrl: z.string().nullable(),
  /** ISO timestamp of when this snapshot was fetched from Google. */
  fetchedAt: z.string().nullable(),
});
export type GoogleReviewsSnapshot = z.infer<typeof GoogleReviewsSnapshotSchema>;
